import React, { useState, useEffect, useCallback } from "react";
import {
  Layout, Table, Button, Space, Select, InputNumber, Row, Col, Tag, message, Descriptions, Popconfirm, Input, Breadcrumb
} from "antd";
import { useParams, useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
  ArrowLeftOutlined, PlusOutlined, SaveOutlined, SendOutlined,
  DeleteOutlined, ShoppingCartOutlined, InfoCircleOutlined, HomeOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import moment from "moment";
import "../../../styles/fluidDesign.css";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { Option } = Select;

const ManagePRItems = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [poMaster, setPoMaster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [prItems, setPrItems] = useState([]);

  // Add Item Fields
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [cost, setCost] = useState(0);
  const [tax, setTax] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [itemNote, setItemNote] = useState("");

  const fetchPOData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inventory/pr/get.php?id=${id}`);
      if (res.data && res.data.status === "success") {
        setPoMaster(res.data.master);
        
        // Map items to match current inventory schema
        const lines = (res.data.items || []).map(item => ({
          invi_id: Number(item.invi_id),
          order_qty: parseFloat(item.order_qty),
          unit_price: parseFloat(item.unit_price),
          tax_price: parseFloat(item.tax_price),
          discount_price: parseFloat(item.discount_price),
          item_note: item.item_note || "",
          inv_code: item.inv_code || "",
          inv_desc: item.inv_desc || ""
        }));
        
        setPrItems(lines);
      } else {
        message.error("Failed to load Purchase Request details.");
      }
    } catch (error) {
      message.error("Error loading Purchase Request.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchInventoryItems = useCallback(async () => {
    try {
      const res = await api.get("/inventory/items/list.php?page=1&limit=1000");
      if (res.data && res.data.status === "success") {
        setInventoryItems(res.data.dataset || []);
      }
    } catch (error) {
      message.error("Failed to load inventory items.");
    }
  }, []);

  useEffect(() => {
    fetchPOData();
    fetchInventoryItems();
  }, [fetchPOData, fetchInventoryItems]);

  const handleItemSelect = (itemId) => {
    setSelectedItemId(itemId);
    const selectedItem = inventoryItems.find(ii => Number(ii.invi_id) === Number(itemId));
    if (selectedItem) {
      setQuantity(1.00);
      setCost(parseFloat(selectedItem.cost_price) || 0.00);
      setTax(0.00);
      setDiscount(0.00);
    }
  };

  const handleAddItem = () => {
    if (!selectedItemId) {
      message.warning("Please select an item.");
      return;
    }
    if (quantity <= 0) {
      message.warning("Quantity must be greater than 0.");
      return;
    }

    const itemExists = prItems.some(item => Number(item.invi_id) === Number(selectedItemId));
    if (itemExists) {
      message.warning("This item is already added. Please remove it first to re-add.");
      return;
    }

    const selectedItem = inventoryItems.find(ii => Number(ii.invi_id) === Number(selectedItemId));
    const newItem = {
      invi_id: Number(selectedItemId),
      order_qty: parseFloat(quantity),
      unit_price: parseFloat(cost),
      tax_price: parseFloat(tax),
      discount_price: parseFloat(discount),
      item_note: itemNote.trim(),
      inv_code: selectedItem?.inv_code || "",
      inv_desc: selectedItem?.inv_desc || ""
    };

    const updated = [...prItems, newItem];
    setPrItems(updated);

    // Reset fields
    setSelectedItemId(null);
    setQuantity(1);
    setCost(0);
    setTax(0);
    setDiscount(0);
    setItemNote("");
    message.success("Item added to Purchase Request.");
  };

  const handleRemoveItem = (invId) => {
    const updated = prItems.filter(item => Number(item.invi_id) !== Number(invId));
    setPrItems(updated);
    message.success("Item removed.");
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;
    prItems.forEach(item => {
      if (item) {
        const qty = parseFloat(item.order_qty) || 0;
        const price = parseFloat(item.unit_price) || 0;
        const itemTax = parseFloat(item.tax_price) || 0;
        const itemDisc = parseFloat(item.discount_price) || 0;

        subtotal += qty * price;
        totalTax += itemTax;
        totalDiscount += itemDisc;
      }
    });
    return {
      subtotal,
      tax: totalTax,
      discount: totalDiscount,
      grandTotal: subtotal + totalTax - totalDiscount
    };
  };

  const totals = calculateTotals();

  const handleSave = async (submitStatus) => {
    if (prItems.length === 0) {
      message.warning("Please add at least one line item.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        pr_id: parseInt(id),
        pr_status: submitStatus,
        delivery_expected_date: poMaster?.delivery_expected_date || null,
        ipo_note: poMaster?.ipo_note || "",
        items: prItems
      };

      const res = await api.post("/inventory/pr/update.php", payload);
      if (res.data && res.data.status === "success") {
        message.success(`Purchase Request saved as ${submitStatus}.`);
        navigate("/inventory-purchase-requests");
      } else {
        message.error(res.data.message || "Failed to save Purchase Request.");
      }
    } catch (error) {
      message.error("Failed to save Purchase Request.");
    } finally {
      setLoading(false);
    }
  };

  const renderStatusTag = (status) => {
    return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
  };

  const isEditable = poMaster && (poMaster.pr_status === "Draft" || poMaster.pr_status === "Rejected");
  const selectedItemDetails = selectedItemId ? inventoryItems.find(ii => Number(ii.invi_id) === Number(selectedItemId)) : null;

  const itemColumns = [
    {
      title: "Item",
      key: "item_details",
      render: (_, record) => {
        const itemDetails = inventoryItems.find(ii => Number(ii.invi_id) === Number(record.invi_id));
        return (
          <div>
            <span style={{ fontWeight: 500 }}>{itemDetails ? itemDetails.inv_desc : `Item ID: ${record.invi_id}`}</span>
            <small style={{ color: '#888', marginLeft: 8 }}>({record.inv_code || itemDetails?.inv_code || "-"})</small>
            {record.item_note && <div style={{ marginTop: 4, color: '#f5222d', fontSize: '12px', fontStyle: 'italic' }}>Note: {record.item_note}</div>}
          </div>
        );
      }
    },
    {
      title: "Qty",
      dataIndex: "order_qty",
      key: "order_qty",
      render: (val) => parseFloat(val || 0).toLocaleString()
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (val) => `LKR ${parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    {
      title: "Tax",
      dataIndex: "tax_price",
      key: "tax_price",
      render: (val) => `LKR ${parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    {
      title: "Discount",
      dataIndex: "discount_price",
      key: "discount_price",
      render: (val) => `LKR ${parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    {
      title: "Total (LKR)",
      key: "item_total",
      render: (_, record) => {
        const qty = parseFloat(record.order_qty) || 0;
        const price = parseFloat(record.unit_price) || 0;
        const tax = parseFloat(record.tax_price) || 0;
        const disc = parseFloat(record.discount_price) || 0;
        const total = (qty * price) + tax - disc;
        return <span style={{ fontWeight: 600, color: "var(--fluid-primary)" }}>LKR {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
      }
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        isEditable && (
          <Popconfirm
            title="Remove this item?"
            onConfirm={() => handleRemoveItem(record.invi_id)}
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        )
      )
    }
  ];

  return (
    <Layout className="fluid-page">
      <Headerdiv />
      
      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <Row align="bottom" justify="space-between">
          <Col>
                          <Breadcrumb
                  className="fluid-breadcrumb"
                  items={[
                      { href: '/dash', title: <HomeOutlined /> },
                      { href: '/inventory-items', title: 'Inventory' },
                      { title: 'PR Items' },
                  ]}
              />
            <h1 className="fluid-page-title">
              <ShoppingCartOutlined /> PR Items: {poMaster ? poMaster.pr_ref : "Loading..."}
            </h1>
          </Col>
          <Col xs={0} md={12} style={{ textAlign: 'right' }}>
            <p className="fluid-page-subtitle">Configure line items for this purchase request</p>
          </Col>
        </Row>
      </div>

      <Content className="fluid-content">
        {poMaster ? (
          <Row gutter={[16, 16]}>
            {/* PR Master Info */}
            <Col span={24}>
              <div className="fluid-card">
                <div className="fluid-card-header">
                  <h3 className="fluid-card-title">
                    <ShoppingCartOutlined style={{ marginRight: 8, color: "var(--fluid-primary)" }} />
                    Purchase Request Overview
                  </h3>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/inventory-purchase-requests")}>
                    Back to List
                  </Button>
                </div>
                <Descriptions column={{ xs: 1, sm: 2, md: 4 }} bordered size="small">
                  <Descriptions.Item label="PR Reference"><strong>{poMaster.pr_ref}</strong></Descriptions.Item>
                  <Descriptions.Item label="Supplier">{poMaster.supplier_name}</Descriptions.Item>
                  <Descriptions.Item label="Date">{moment(poMaster.pr_date).format("YYYY-MM-DD")}</Descriptions.Item>
                  <Descriptions.Item label="Expected Delivery">{poMaster.delivery_expected_date ? moment(poMaster.delivery_expected_date).format("YYYY-MM-DD") : "-"}</Descriptions.Item>
                  <Descriptions.Item label="Status">{renderStatusTag(poMaster.pr_status)}</Descriptions.Item>
                  <Descriptions.Item label="Created By">{poMaster.creator_name || "-"}</Descriptions.Item>
                  <Descriptions.Item label="PR Note" span={2}>{poMaster.ipo_note || "-"}</Descriptions.Item>
                </Descriptions>
              </div>
            </Col>

            {/* Add Item Form */}
            {isEditable && (
              <Col span={24}>
                <div className="fluid-card">
                  <div className="fluid-card-header">
                    <h3 className="fluid-card-title">
                      <PlusOutlined style={{ marginRight: 8, color: "var(--fluid-primary)" }} />
                      Add Item to Request
                    </h3>
                  </div>
                  <Row gutter={16} align="bottom" className="fluid-add-item-row">
                    <Col xs={24} md={8}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: "var(--fluid-text)" }}>Select Inventory Item</label>
                      <Select allowClear
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Search item by code or description"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          const label = option?.label || option?.children;
                          if (Array.isArray(label)) {
                            return label.join('').toLowerCase().includes(input.toLowerCase());
                          }
                          return String(label || '').toLowerCase().includes(input.toLowerCase());
                        }}
                        value={selectedItemId}
                        onChange={handleItemSelect}
                      >
                        {inventoryItems.map(ii => (
                          <Option key={ii.invi_id} value={ii.invi_id}>
                            {ii.inv_code} - {ii.inv_desc} (Stock: {ii.available_qty})
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={12} md={4}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: "var(--fluid-text)" }}>Quantity</label>
                      <InputNumber min={0.01} style={{ width: '100%' }} value={quantity} onChange={setQuantity} />
                    </Col>
                    <Col xs={12} md={4}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: "var(--fluid-text)" }}>Unit Price</label>
                      <InputNumber min={0} style={{ width: '100%' }} value={cost} onChange={setCost} />
                    </Col>
                    <Col xs={12} md={4}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: "var(--fluid-text)" }}>Tax</label>
                      <InputNumber min={0} style={{ width: '100%' }} value={tax} onChange={setTax} />
                    </Col>
                    <Col xs={12} md={4}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: "var(--fluid-text)" }}>Discount</label>
                      <InputNumber min={0} style={{ width: '100%' }} value={discount} onChange={setDiscount} />
                    </Col>
                  </Row>
                  
                  <Row gutter={16} style={{ marginTop: 16 }} className="fluid-add-item-row">
                    <Col xs={24}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: "var(--fluid-text)" }}>Item Note (Optional)</label>
                      <Input
                        placeholder="Enter item specifications or instructions..."
                        value={itemNote}
                        onChange={(e) => setItemNote(e.target.value)}
                      />
                    </Col>
                  </Row>
                  
                  {selectedItemDetails && (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <InfoCircleOutlined style={{ color: 'var(--fluid-primary)' }} />
                        <span style={{ fontWeight: 600, color: "var(--fluid-text)" }}>Item Details</span>
                      </div>
                      <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                        <Descriptions.Item label="Code">{selectedItemDetails.inv_code}</Descriptions.Item>
                        <Descriptions.Item label="Description">{selectedItemDetails.inv_desc}</Descriptions.Item>
                        <Descriptions.Item label="Cost Price">LKR {parseFloat(selectedItemDetails.cost_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                        <Descriptions.Item label="Available Stock">
                          <strong style={{ color: parseInt(selectedItemDetails.available_qty) > 0 ? 'var(--fluid-primary)' : '#ff4d4f' }}>
                            {parseInt(selectedItemDetails.available_qty).toLocaleString()}
                          </strong>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  )}
                  
                  <Row justify="space-between" align="middle" style={{ marginTop: 16 }} className="fluid-add-item-row">
                    <Col>
                      {selectedItemId && (
                        <div style={{ fontSize: '15px', color: 'var(--fluid-text)' }}>
                          Line Total: <strong style={{ color: 'var(--fluid-primary)', fontSize: '16px' }}>
                            LKR {(((parseFloat(quantity) || 0) * (parseFloat(cost) || 0)) + (parseFloat(tax) || 0) - (parseFloat(discount) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </strong>
                        </div>
                      )}
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        className="fluid-btn-primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddItem}
                        disabled={!selectedItemId}
                      >
                        Add Item
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Col>
            )}

            {/* Items List & Totals */}
            <Col span={24}>
              <div className="fluid-card">
                <div className="fluid-card-header">
                  <h3 className="fluid-card-title">
                    <ShoppingCartOutlined style={{ marginRight: 8, color: "var(--fluid-primary)" }} />
                    Line Items ({prItems.length})
                  </h3>
                  {!isEditable && <Tag color="warning" style={{ fontWeight: 600 }}>READ ONLY</Tag>}
                </div>
                <div className="fluid-table">
                  <Table
                    columns={itemColumns}
                    dataSource={prItems}
                    rowKey={(record) => record.invi_id || Math.random()}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    style={{ marginBottom: 20 }}
                  />
                </div>

                {prItems.length > 0 && (
                  <div style={{ background: "var(--fluid-bg-light)", padding: 16, borderRadius: 8, marginTop: 16 }}>
                    <Row gutter={16} justify="end">
                      <Col xs={24} md={12}>
                        <Descriptions column={1} size="small" bordered>
                          <Descriptions.Item label="Sub Total">LKR {totals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                          <Descriptions.Item label="Total Tax">LKR {totals.tax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                          <Descriptions.Item label="Total Discount">LKR {totals.discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                          <Descriptions.Item label={<strong style={{ color: "var(--fluid-primary)" }}>Grand Total</strong>}>
                            <strong style={{ color: "var(--fluid-primary)" }}>LKR {totals.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                          </Descriptions.Item>
                        </Descriptions>
                      </Col>
                    </Row>
                  </div>
                )}

                {isEditable && (
                  <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid var(--fluid-border)' }}>
                    <Space size="middle">
                      <Button
                        type="default"
                        icon={<SaveOutlined />}
                        onClick={() => handleSave("Draft")}
                        loading={loading}
                        style={{ minWidth: '150px' }}
                      >
                        Save as Draft
                      </Button>
                      {prItems.length > 0 && (
                        <Popconfirm
                          title="Submit this Purchase Request for approval?"
                          onConfirm={() => handleSave("Pending")}
                        >
                          <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={loading}
                            className="fluid-btn-primary"
                            style={{ minWidth: '150px' }}
                          >
                            Submit
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        ) : (
          <div className="fluid-card" style={{ textAlign: 'center', padding: 40 }}>
            {loading ? "Loading..." : "Purchase Request not found"}
          </div>
        )}
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default ManagePRItems;
